// © Microsoft Corporation. All rights reserved.

import { LiteLogger } from "./LiteLogger";
import { ILoggingService } from "clientcore-infrastructure-analytics/ILoggingService";

/**
 * Interface for logger so different implementation of logger could be used.
 */
export interface Logger {

    /**
     * Returns the logging service
     * @returns {ILoggingService}
     */
    getLoggingService(): ILoggingService;

    /**
     * Checks whether we're running in debug mode.
     * @returns True when either debug=1 or debug=true is present as a query param, otherwise false.
     */
    isDebug(): boolean;

    /**
     * Override the isDebug setting. Useful when server side rendering.
     */
    setDebug(isDebug: boolean): void;

    /**
     * Set the tags to filter logs.
     * @param tags Tags to filter logs. If empty, do not filter.
     */
    setTags(tags: string[]): void;

    /**
     * Log console message.
     * Note that these are not output to the console unless you're in debug mode.
     * @param message - Message to log.
     * @param tags - Optional tags for specifying log type for filtering logs.
     */
    log(message: string, tags?: string[]): void;

    /**
     * Log error message.
     * @param message - Message to log
     */
    logError(message: string): void;

    /**
     * Lazy log console message. Use this when the message being constructed is complex or expensive so that it's only evaluated if needed.
     * Note that the callback is not invoked and the message is not output unless you're in debug mode.
     * @param getMessage - The function to call to get message to log
     */
    logCallback(getMessage: () => string): void;

    /**
     * Log the object to the console.
     * Note that these are not output to the console unless you're in debug mode.
     * @param message - Array of messages to log
     */
    logObjects(...message: any[]): void;
}

/**
 * Set the logger instance.
 * @param _logger The logger instance to change to.
 */
export function setLogger(_logger: Logger): void {
    logger = _logger;
}

/** Logger used by everyone. Default is LiteLogger. Please set up desired logger implementaiton with setLogger(). */
export let logger: Logger = new LiteLogger();